import {InteractionRequiredAuthError} from "@azure/msal-browser";

// Ошибки, при которых popup не смог открыться (заблокирован браузером) -> пробуем redirect.
// На остальные ошибки popup (user_cancelled, interaction_in_progress, ...) redirect НЕ делаем,
// иначе получаем серию перезагрузок страницы.
const POPUP_BLOCKED_CODES = ['popup_window_error', 'empty_window_error'];
// Повторные запросы на обновление в пределах этого окна получают последний токен без похода в Azure.
const RECENT_REFRESH_MS = 10000;

let refresher = null;
let inflight = null;
let lastToken = null;
let lastRefreshAt = 0;

export const setTokenRefresher = (fn) => {
    refresher = fn;
};

export const resetTokenRefresher = () => {
    refresher = null;
    inflight = null;
    lastToken = null;
    lastRefreshAt = 0;
};

/**
 * Единая точка обновления токена: параллельные вызовы (старт, интервал, 401 от разных запросов,
 * App) получают один и тот же промис, поэтому одновременно идёт не больше одного запроса
 * в Azure и не больше одного popup/redirect.
 */
export const refreshTokenOnce = ({force = false} = {}) => {
    if (!refresher) return Promise.resolve(null);
    if (inflight) return inflight;
    if (!force && lastToken && Date.now() - lastRefreshAt < RECENT_REFRESH_MS) return Promise.resolve(lastToken);

    inflight = Promise.resolve()
        .then(refresher)
        .then(token => {
            if (token) {
                lastToken = token;
                lastRefreshAt = Date.now();
            }
            return token || null;
        }, error => {
            console.error('token refresh failed', error);
            return null;
        })
        .finally(() => {
            inflight = null;
        });
    return inflight;
};

const getAccount = (msalInstance) => msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null;

const storeToken = (dispatch, storage, data) => {
    dispatch({
        type: 'AUTH_SELF_SUCCESS', payload: {
            data: data.account.idTokenClaims.roles,
            token: data.idToken,
            email: data.account.username
        }
    });
    storage.setItem("azureToken", data.idToken);
    storage.setItem("username", data.account.username);
};

export const createMsalTokenRefresher = ({msalInstance, dispatch, storage, sentry, origin}) => async () => {
    const account = getAccount(msalInstance);
    if (!account) return null;

    // refreshTokenExpirationOffsetSeconds не задаём (по умолчанию 5 минут): у SPA refresh token живёт 24ч,
    // и прежнее значение 86400 заставляло MSAL считать его всегда истёкшим и каждый раз идти через iframe.
    const request = {
        account,
        forceRefresh: true,
        scopes: ['user.read'],
        redirectUri: origin + "/dummy.html",
    };

    try {
        const data = await msalInstance.acquireTokenSilent(request);
        storeToken(dispatch, storage, data);
        return data.idToken;
    } catch (e) {
        if (e?.errorCode === 'no_network_connectivity') {
            console.error('token refresh: no network');
            return null;
        }

        if (e instanceof InteractionRequiredAuthError) {
            try {
                const response = await msalInstance.acquireTokenPopup(request);
                sentry.captureMessage(`Token acquireTokenPopup ${JSON.stringify(response)}`, sentry.Severity.Info);
                if (response?.account) storeToken(dispatch, storage, response);
                return response?.idToken || null;
            } catch (popupError) {
                if (POPUP_BLOCKED_CODES.includes(popupError?.errorCode)) {
                    sentry.captureMessage(`Token acquireTokenRedirect ${JSON.stringify(popupError)}`, sentry.Severity.Info);
                    await msalInstance.acquireTokenRedirect({...request, redirectUri: origin + "/"});
                } else {
                    console.error('token popup error', popupError);
                }
                return null;
            }
        }

        dispatch({type: 'AUTH_SELF_FAILURE', payload: {error: ''}});
        sentry.captureMessage(`Token not refreshed ${JSON.stringify(e)}`, sentry.Severity.Error);
        return null;
    }
};

/**
 * Оборачивает baseQuery: при 401 обновляет токен (single-flight) и один раз повторяет запрос,
 * чтобы одноразовые запросы (environments, types, ...) не оставались в ошибке после истечения токена.
 */
export const withAuthRetry = (baseQuery, storage) => async (args, api, extraOptions) => {
    const usedToken = storage.getItem("azureToken");
    let result = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
        console.error("Unauthorized access detected");
        const token = await refreshTokenOnce();
        if (!token) {
            storage.removeItem("azureToken");
        } else if (token !== usedToken) {
            result = await baseQuery(args, api, extraOptions);
        }
    }

    return result;
};
